import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { BlindMatchStatusBadge } from 'browser/components/atomic-elements/atoms/blind-match/blind-match-status-badge'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

import {
  UI2BlindMatchFieldProps,
  UI2_BLIND_MATCH_FIELD_DEFAULT_PROPS,
  BlindMatchStatus,
  BlindMatchValue,
  BlindMatchUserEdge,
  computeBlindMatchStatus,
  resolveSourceValue,
} from 'shared-libs/components/ui2/fields/ui2-blind-match-field'
import {
  IFieldShellEditableExtras,
} from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-props'
import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2FieldControl } from '../field-ui/field-control/field-control'
import { UI2ActionJustificationPanel } from '../field-ui/action-justification-panel/action-justification-panel'

import './_ui2-blind-match-field.scss'

interface IUI2BlindMatchFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  errors?: any[]
  frames?: FramesManager
  translationTable?: any
}

export interface IUI2BlindMatchFieldProps
  extends UI2BlindMatchFieldProps,
    IUI2BlindMatchFieldWebExtras {}

interface IState {
  typedValue: string
  sourceValue: any
  isFetchingSource: boolean
  fetchErrored: boolean
  overrideReason: string | undefined
  isSourceRevealed: boolean
  isOtherModeActive: boolean
  isOverrideConfirmed: boolean
}

export class UI2BlindMatchField extends React.Component<IUI2BlindMatchFieldProps, IState> {
  public static defaultProps: Partial<IUI2BlindMatchFieldProps> = {
    ...UI2_BLIND_MATCH_FIELD_DEFAULT_PROPS,
  }

  private static VALIDATE_DEBOUNCE_MS = 350
  private validateTimer: ReturnType<typeof setTimeout> | null = null

  constructor(props: IUI2BlindMatchFieldProps) {
    super(props)
    this.state = {
      typedValue: props.value?.value ?? '',
      sourceValue: undefined,
      isFetchingSource: false,
      fetchErrored: false,
      overrideReason: props.value?.overrideReason,
      isSourceRevealed: false,
      isOtherModeActive: false,
      isOverrideConfirmed: props.value?.overridden === true,
    }
  }

  public componentDidMount(): void {
    void this.resolveAndCompute()
  }

  public componentDidUpdate(prev: IUI2BlindMatchFieldProps): void {
    const { source, sourcePath, entity, value } = this.props
    if (
      !_.isEqual(source, prev.source) ||
      !_.isEqual(sourcePath, prev.sourcePath) ||
      entity?.entityId !== prev.entity?.entityId
    ) {
      void this.resolveAndCompute()
    }
    if (!_.isEqual(prev.value, value)) {
      const nextTyped = value?.value ?? ''
      const nextReason = value?.overrideReason
      // Skip sync while the debounce timer is active — the user is still
      // typing and the echoed-back prop would overwrite their in-progress input.
      if (this.validateTimer === null && (nextTyped !== this.state.typedValue || nextReason !== this.state.overrideReason)) {
        this.setState({
          typedValue: nextTyped,
          overrideReason: nextReason,
          isOverrideConfirmed: value?.overridden === true ? true : this.state.isOverrideConfirmed,
        })
      }
    }
  }

  public componentWillUnmount(): void {
    if (this.validateTimer) clearTimeout(this.validateTimer)
  }

  // ─── Status ─────────────────────────────────────────────────────────────

  private computeStatus(): BlindMatchStatus {
    const { showUnavailableOnEmptySource } = this.props
    const { sourceValue, typedValue, overrideReason, isFetchingSource, fetchErrored } = this.state
    if (isFetchingSource) return BlindMatchStatus.Loading
    if (fetchErrored) return BlindMatchStatus.FetchError
    // Show Validating while the debounce timer is active so the badge does
    // not flip on every keystroke.
    if (this.validateTimer !== null && typedValue) return BlindMatchStatus.Validating
    const { status } = computeBlindMatchStatus({ sourceValue, typedValue, showUnavailableOnEmptySource })
    if (status === BlindMatchStatus.Failed && !_.isEmpty(overrideReason?.trim())) {
      return BlindMatchStatus.Overridden
    }
    return status
  }

  private mergedErrors(): any[] | undefined {
    const { errors, allowOverride, matchFailedErrorText } = this.props
    if (!_.isEmpty(errors) && !allowOverride && this.computeStatus() === BlindMatchStatus.Failed) {
      return [matchFailedErrorText ?? 'The entered value must match the source value.']
    }
    return errors
  }

  private overrideReasonErrors(): any[] | undefined {
    const errorsMap = (this.props as any).errorsMap
    const child = errorsMap?.overrideReason
    return child && _.isArray(child._errors) && child._errors.length > 0 ? child._errors : undefined
  }

  private shouldShowBadge(status: BlindMatchStatus): boolean {
    const { showUnavailableOnEmptySource, isDisabled } = this.props
    const { sourceValue } = this.state
    if (isDisabled) return false
    if (!showUnavailableOnEmptySource && (sourceValue === undefined || sourceValue === null)) return false
    return true
  }

  // ─── Source resolution ───────────────────────────────────────────────────

  private resolveAndCompute = async (): Promise<void> => {
    const { source, sourcePath, entity, showUnavailableOnEmptySource } = this.props
    const needsAsync =
      !source && sourcePath && entity?.entityId && typeof entity.get !== 'function'
    if (needsAsync) {
      this.setState({ isFetchingSource: true })
    }
    const resolved = await resolveSourceValue(
      { source, sourcePath, entity, showUnavailableOnEmptySource },
      (id) => apis.getStore().getOrFetchRecord(id)
    )
    if (resolved.errored) {
      this.setState({ isFetchingSource: false, fetchErrored: true, sourceValue: undefined })
      return
    }
    this.setState(
      { sourceValue: resolved.value, isFetchingSource: false, fetchErrored: false },
      () => this.emitForCurrent()
    )
  }

  // ─── Emit ────────────────────────────────────────────────────────────────

  private emitForCurrent(): void {
    const { onChange, value: current, showUnavailableOnEmptySource } = this.props
    const { sourceValue, typedValue, overrideReason, fetchErrored } = this.state
    if (fetchErrored || !onChange) return
    const { status, verified } = computeBlindMatchStatus({
      sourceValue,
      typedValue,
      showUnavailableOnEmptySource,
    })
    let next: BlindMatchValue | undefined
    if (
      status === BlindMatchStatus.Precheck &&
      (sourceValue === undefined || sourceValue === null) &&
      !showUnavailableOnEmptySource
    ) {
      next = typedValue ? { value: typedValue, verified: undefined } : undefined
    } else if (!typedValue) {
      next = undefined
    } else if (verified) {
      next = { value: typedValue, verified: true }
    } else if (!_.isEmpty(overrideReason?.trim()) && sourceValue != null && sourceValue !== '') {
      // Only treat as an intentional override when there is actually a source
      // value the user is overriding — no source means no mismatch to override.
      next = {
        value: typedValue,
        verified: true,
        overridden: true,
        overrideReason,
        overriddenBy: this.getOverriddenBy(),
      }
    } else {
      next = { value: typedValue, verified: false }
    }
    if (_.isEqual(next, current)) return
    onChange(next)
  }

  private getOverriddenBy(): BlindMatchValue['overriddenBy'] {
    try {
      const user: any = apis.getSettings?.()?.getUser?.()
      const entityId: string | undefined = user?.uniqueId ?? user?.get?.('uniqueId')
      if (!entityId) return undefined
      const displayName: string | undefined = user?.displayName ?? user?.get?.('displayName')
      const edge: BlindMatchUserEdge = displayName ? { entityId, displayName } : { entityId }
      return { user: edge }
    } catch {
      return undefined
    }
  }

  // ─── Handlers ────────────────────────────────────────────────────────────

  private handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.value
    // Reset override confirmation when the user changes the value — they may
    // be correcting the mismatch, so the confirm step should repeat.
    this.setState({ typedValue: next, isOverrideConfirmed: false })
    if (this.validateTimer) clearTimeout(this.validateTimer)
    this.validateTimer = setTimeout(() => {
      this.validateTimer = null
      this.emitForCurrent()
    }, UI2BlindMatchField.VALIDATE_DEBOUNCE_MS)
  }

  private handleOverrideReasonChange = (next: string | undefined): void => {
    this.setState({ overrideReason: next }, () => this.emitForCurrent())
  }

  private handleOtherModeChange = (isOtherModeActive: boolean): void => {
    if (this.state.isOtherModeActive === isOtherModeActive) return
    this.setState({ isOtherModeActive })
  }

  private handleOverrideToggle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = e.target.checked
    if (checked) {
      this.setState({ isOverrideConfirmed: true })
    } else {
      this.setState({ isOverrideConfirmed: false, overrideReason: undefined }, () => this.emitForCurrent())
    }
  }

  private toggleSourceReveal = (): void => {
    this.setState((s) => ({ isSourceRevealed: !s.isSourceRevealed }))
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  public render(): React.ReactNode {
    const { className } = this.props
    const merged = this.mergedErrors()
    return (
      <UI2FieldShell
        {...this.props}
        errors={merged}
        isValueEmpty={(v) => !v?.value}
        renderReadOnly={(v, { editNode }) => this.renderReadOnly(v, editNode)}
        renderEditable={(_, extras) => this.renderEditable(merged, extras)}
        className={classNames('ui2-blindMatchField', className)}
      >
        {null}
      </UI2FieldShell>
    )
  }

  private renderReadOnly(
    value: BlindMatchValue | undefined,
    editNode: React.ReactNode
  ): React.ReactNode {
    const { statusTooltips } = this.props
    const stringValue = value?.value
    if (!stringValue) {
      return (
        <span className='ui2-blindMatchField__readOnlyEmpty'>No value available</span>
      )
    }
    const readOnlyStatus = value?.overridden
      ? BlindMatchStatus.Overridden
      : value?.verified
        ? BlindMatchStatus.Verified
        : null
    return (
      <div className='ui2-blindMatchField__readOnly'>
        <div className='ui2-blindMatchField__readOnlyRow'>
          <span className='ui2-blindMatchField__readOnlyValue'>{stringValue}</span>
          {readOnlyStatus && (
            <BlindMatchStatusBadge status={readOnlyStatus as any} statusTooltips={statusTooltips} />
          )}
          {value?.verified === true && editNode}
        </div>
        {value?.overridden && value?.overrideReason && (
          <div className='ui2-blindMatchField__readOnlyReason'>{value.overrideReason}</div>
        )}
      </div>
    )
  }

  private renderEditable(
    merged: any[] | undefined,
    extras: IFieldShellEditableExtras
  ): React.ReactNode {
    const {
      placeholder,
      isDisabled,
      statusTooltips,
      allowOverride,
      allowRevealSource,
      frames,
      translationTable,
      overrideReasons,
      overrideToggleLabel,
    } = this.props
    const { typedValue, sourceValue, isSourceRevealed, overrideReason, isOtherModeActive, isOverrideConfirmed } =
      this.state
    const status = this.computeStatus()
    const showBadge = this.shouldShowBadge(status)
    const effectivePlaceholder = status === BlindMatchStatus.Unavailable ? '' : placeholder

    const overrideVisible =
      allowOverride &&
      (status === BlindMatchStatus.Failed || status === BlindMatchStatus.Overridden)
    const revealVisible =
      allowRevealSource &&
      sourceValue != null &&
      sourceValue !== '' &&
      (status === BlindMatchStatus.Failed || status === BlindMatchStatus.Overridden)

    const hasReasonOptions = !!(overrideReasons?.length)
    const showOverridePanel = overrideVisible && isOverrideConfirmed
    const effectiveOverrideLabel = overrideToggleLabel ?? 'Override source value?'

    const footer =
      overrideVisible || revealVisible ? (
        <div className='ui2-blindMatchField__footer'>
          {revealVisible && (
            <button
              type='button'
              className={classNames('ui2-blindMatchField__revealToggle', {
                'ui2-blindMatchField__revealToggle--shown': isSourceRevealed,
              })}
              onClick={this.toggleSourceReveal}
              aria-expanded={isSourceRevealed}
              aria-label={isSourceRevealed ? 'Hide source value' : 'Reveal source value'}
            >
              <svg
                className='ui2-blindMatchField__revealIcon'
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
                focusable={false}
              >
                {isSourceRevealed ? (
                  <>
                    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
                    <line x1='1' y1='1' x2='23' y2='23' />
                  </>
                ) : (
                  <>
                    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
                    <circle cx='12' cy='12' r='3' />
                  </>
                )}
              </svg>
              {isSourceRevealed ? (
                <span className='ui2-blindMatchField__revealValue'>{String(sourceValue)}</span>
              ) : (
                <span>Reveal</span>
              )}
            </button>
          )}
          {overrideVisible && (
            <label className='ui2-blindMatchField__overrideSwitch'>
              <input
                type='checkbox'
                className='ui2-switchField-input'
                role='switch'
                checked={isOverrideConfirmed}
                disabled={!!isDisabled}
                onChange={this.handleOverrideToggle}
              />
              <span
                className={classNames('ui2-switchField-track', {
                  'ui2-switchField-track--checked': isOverrideConfirmed,
                  'ui2-switchField-track--disabled': !!isDisabled,
                })}
                aria-hidden='true'
              >
                <span className='ui2-switchField-thumb' />
              </span>
              <span className='ui2-blindMatchField__overrideSwitchLabel'>{effectiveOverrideLabel}</span>
            </label>
          )}
          {showOverridePanel && (
            <UI2ActionJustificationPanel
              reasons={overrideReasons?.map((r) =>
                typeof r === 'string' ? { label: r, value: r } : r
              )}
              reason={overrideReason}
              label='Override reason'
              placeholder='Please explain…'
              isDisabled={isDisabled}
              isPickerRequired={hasReasonOptions}
              isFreeformRequired={isOtherModeActive || !hasReasonOptions}
              errors={this.overrideReasonErrors()}
              frames={frames}
              translationTable={translationTable}
              onReasonChange={this.handleOverrideReasonChange}
              onOtherModeChange={this.handleOtherModeChange}
            />
          )}
        </div>
      ) : undefined

    return (
      <UI2FieldControl
        rightAddon={
          showBadge ? (
            <BlindMatchStatusBadge status={status as any} statusTooltips={statusTooltips} />
          ) : undefined
        }
        footer={footer}
      >
        <input
          type='text'
          value={typedValue}
          placeholder={effectivePlaceholder}
          disabled={
            isDisabled ||
            status === BlindMatchStatus.Loading ||
            status === BlindMatchStatus.FetchError
          }
          onChange={this.handleInputChange}
          data-testid={this.props.testID}
          aria-label={this.props.accessibilityLabel}
        />
      </UI2FieldControl>
    )
  }
}
